'use client';

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  ChangeEvent,
  FocusEvent,
} from 'react';
import { useRouter, usePathname } from 'next/navigation';
import useContact from '@/hooks/useContact';
import { LeadInput } from '@jlg-website/shared';
import type {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';

const pathPrefix = '/forms/pi';

export type ComponentName =
  | 'WhatHappened'
  | 'WereYouInjured'
  | 'TellUsAboutYourself'
  | 'HeresWhatsNext';

type TStep = {
  component: ComponentName;
  url: string;
};

const steps: TStep[] = [
  { component: 'WhatHappened', url: 'what-happened' },
  { component: 'WereYouInjured', url: 'were-you-injured' },
  { component: 'TellUsAboutYourself', url: 'tell-us-about-yourself' },
  { component: 'HeresWhatsNext', url: 'heres-whats-next' },
];

interface StepsContextProps {
  consent: string;
  getValues: UseFormGetValues<LeadInput>;
  setValue: UseFormSetValue<LeadInput>;
  register: UseFormRegister<LeadInput>;
  trigger: UseFormTrigger<LeadInput>;
  errors: FieldErrors<LeadInput>;
  caseTypeValue: (string | number | boolean)[];
  wereYouInjuredValue: (string | number | boolean)[];
  phoneNumberValue: string;
  zipCodeValue: string;
  consentValue: boolean;
  errorClassNames: string;
  onCaseTypeChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onWereYouInjuredChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onZipCodeBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onPhoneNumberBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onPhoneNumberChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  nextStep: () => void;
  previousStep: () => void;
  submitLead: (payload: LeadInput) => void;
  isFetching: boolean;
  isLeadSubmitted: boolean;
  isLeadFailed: boolean;
  progress: number;
  step: TStep;
  steps: TStep[];
  stepIndex: number;
  direction: 1 | -1;
}

const StepsContext = createContext<StepsContextProps | undefined>(undefined);

export function StepsProvider({ children, consent }: { children: ReactNode; consent: string }) {
  const pathname = usePathname();
  const {
    getValues,
    setValue,
    isFetching,
    isSucceeded,
    isFailed,
    errors,
    onCaseTypeChange,
    onWereYouInjuredChange,
    caseTypeValue,
    wereYouInjuredValue,
    register,
    onZipCodeBlur,
    onPhoneNumberBlur,
    onPhoneNumberChange,
    phoneNumberValue,
    zipCodeValue,
    consentValue,
    trigger,
    onSubmit,
  } = useContact();
  const [direction, setDirection] = useState<1 | -1>(1);

  const router = useRouter();

  const stepIndex = useMemo(() => {
    const index = steps.findIndex(({ url }) => pathname === `${pathPrefix}/${url}`);
    return index < 0 ? 0 : index;
  }, [pathname]);

  const progress = useMemo(() => {
    return ((stepIndex + 1) * 100) / steps.length;
  }, [stepIndex]);

  const step = useMemo(() => {
    return steps[stepIndex];
  }, [stepIndex]);

  const nextStep = () => {
    if (stepIndex < steps.length - 1) {
      setDirection(1);
      const next = steps[stepIndex + 1];
      const newUrl = `${pathPrefix}/${next.url}`;
      router.push(newUrl);
    }
  };

  const previousStep = () => {
    if (stepIndex > 0) {
      setDirection(-1);
      const previous = steps[stepIndex - 1];
      const newUrl = `${pathPrefix}/${previous.url}`;
      router.push(newUrl);
    }

    if (stepIndex === 0) {
      router.push('/home-with-multi-step-form');
    }
  };

  const errorClassNames =
    'ring-brand-accent-error! text-brand-accent-error! placeholder:text-brand-accent-error/50!';

  return (
    <StepsContext.Provider
      value={{
        nextStep,
        submitLead: onSubmit,
        isFetching: isFetching,
        isLeadSubmitted: isSucceeded,
        isLeadFailed: isFailed,
        step,
        stepIndex,
        steps,
        progress,
        previousStep,
        direction,
        getValues,
        setValue,
        errors,
        onCaseTypeChange,
        onWereYouInjuredChange,
        caseTypeValue,
        wereYouInjuredValue,
        register,
        onZipCodeBlur,
        onPhoneNumberBlur,
        onPhoneNumberChange,
        phoneNumberValue,
        zipCodeValue,
        consentValue,
        trigger,
        errorClassNames,
        consent,
      }}
    >
      {children}
    </StepsContext.Provider>
  );
}

export function useStepsContext() {
  const context = useContext(StepsContext);
  if (context === undefined) {
    throw new Error('useStepsContext must be used within a StepsProvider');
  }
  return context;
}
